import { Injectable, inject } from '@angular/core';
import { createLogger } from '@hunting-nature/shared-core/logging';
import type { FileInfo } from '@capacitor/filesystem';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { catchError, firstValueFrom, of } from 'rxjs';

import type { HuntingProductGalleryMedia } from '../models/hunting-product-gallery-media.interface';
import type { HuntingProductMedia } from '../models/hunting-product-media.model';
import type { MediaListEntry } from '../models/hunting-product-media-list-entry.interface';
import { BackendStateValues, parseCapturedAtFromFileName, SyncStateValues } from '../models/hunting-product-media.model';

import { FileService, HUNTING_PRODUCT_MEDIA_PARTIAL_SUFFIX, HUNTING_PRODUCT_MEDIA_ROOT } from './file.service';

// The firmware stages a video as `<name>.mp4.tmp` while it is still recording and only renames it to
// `.mp4` on finalization (file_muxer.cpp:74, media_service.cpp:1865-1876) — matching the extension
// keeps a mid-write file from being pulled and dropped in as a finished one.
export function isMediaFileName(fileName: string): boolean {
  return (fileName.startsWith('img_') && fileName.endsWith('.jpg')) || (fileName.startsWith('vid_') && fileName.endsWith('.mp4'));
}

// Slicing on `serial.length + 1` (rather than a plain id.split('/')) keeps a multi-segment serial intact.
export function parseMediaItemId(id: string, serial: string): { yearMonth: string; filename: string } {
  const relative = id.slice(serial.length + 1);
  const slashIndex = relative.indexOf('/');
  return {
    yearMonth: relative.slice(0, slashIndex),
    filename: relative.slice(slashIndex + 1),
  };
}

function mediaTypeFromFileName(fileName: string): 'image' | 'video' {
  return fileName.startsWith('img_') ? 'image' : 'video';
}

@Injectable({
  providedIn: 'root',
})
export class HuntingProductMediaStore {
  private readonly fileService = inject(FileService);
  private readonly log = createLogger('HuntingProductMediaStore');

  /** The filesystem is the source of truth; syncState is derived from what's actually on disk. */
  async list$(serial: string): Promise<Array<HuntingProductMedia>> {
    const entries = await this.listWithSizes$(serial);
    return entries.map(({ media }) => media);
  }

  async listGallery$(serial: string): Promise<Array<HuntingProductGalleryMedia>> {
    const entries = await this.listWithSizes$(serial);
    return Promise.all(entries.map(entry => this.withDisplayData$(entry)));
  }

  async toGalleryMedia$(media: HuntingProductMedia): Promise<HuntingProductGalleryMedia> {
    const sizeBytes = await firstValueFrom(this.fileService.statDataFileSize$(media.localFilePath).pipe(catchError(() => of(undefined))));
    return this.withDisplayData$({ media, sizeBytes });
  }

  async listAll$(): Promise<Array<HuntingProductMedia>> {
    const serialEntries = await this.readdirSafe$(HUNTING_PRODUCT_MEDIA_ROOT);
    const serialDirectories = serialEntries.filter(entry => entry.type === 'directory');
    const perSerial = await Promise.all(serialDirectories.map(entry => this.list$(entry.name)));
    return perSerial.flat();
  }

  /** Also builds the remote-side item for a device listing (sync effects); keep it the single parser. */
  buildMediaItem(serial: string, yearMonth: string, fileName: string): HuntingProductMedia {
    return {
      id: `${serial}/${yearMonth}/${fileName}`,
      serial,
      capturedAt: parseCapturedAtFromFileName(fileName),
      type: mediaTypeFromFileName(fileName),
      localFilePath: this.fileService.huntingProductMediaPath(serial, yearMonth, fileName),
      thumbnailPath: null,
      syncState: SyncStateValues.PENDING,
      backendState: BackendStateValues.LOCAL,
      uploadedAt: null,
    };
  }

  async deleteAll$(): Promise<void> {
    await firstValueFrom(
      this.fileService.deleteDataDirectory$(HUNTING_PRODUCT_MEDIA_ROOT).pipe(
        catchError((error: unknown) => {
          const message = error instanceof Error ? error.message : String(error);
          // Capacitor's own wording for "nothing to delete" — anything else is a real failure worth surfacing
          if (!message.includes('does not exist')) {
            this.log.error('deleteAll$: rmdir failed', { error });
          }
          return of(undefined);
        })
      )
    );
  }

  private async listWithSizes$(serial: string): Promise<Array<MediaListEntry>> {
    const serialPath = `${HUNTING_PRODUCT_MEDIA_ROOT}/${serial}`;
    const monthEntries = await this.readdirSafe$(serialPath);
    const monthDirectories = monthEntries.filter(entry => entry.type === 'directory' && /^\d{4}-\d{2}$/.test(entry.name));

    const entries: Array<MediaListEntry> = [];
    for (const monthDirectory of monthDirectories) {
      entries.push(...(await this.listMonth$(serial, monthDirectory.name)));
    }
    return entries;
  }

  private async withDisplayData$({ media, sizeBytes }: MediaListEntry): Promise<HuntingProductGalleryMedia> {
    if (media.syncState !== SyncStateValues.COMPLETE) {
      return { ...media, displaySrc: undefined, sizeBytes: undefined };
    }
    const displaySource = await firstValueFrom(this.fileService.getDataFileDisplaySrc$(media.localFilePath).pipe(catchError(() => of(undefined))));
    return { ...media, displaySrc: displaySource, sizeBytes };
  }

  private async listMonth$(serial: string, yearMonth: string): Promise<Array<MediaListEntry>> {
    const fileEntries = await this.readdirSafe$(`${HUNTING_PRODUCT_MEDIA_ROOT}/${serial}/${yearMonth}`);
    const finalSizes = new Map<string, number>();
    // value = on-disk name (with the cycle suffix) so localFilePath points at the file that is there
    const partialEntries = new Map<string, string>();

    for (const entry of fileEntries) {
      if (entry.type === 'directory') continue;
      // Suffix marker, not endsWith: the on-disk name is `<fileName>.partial-<cycleId>`, not a bare `.partial`.
      const partialIndex = entry.name.indexOf(HUNTING_PRODUCT_MEDIA_PARTIAL_SUFFIX);
      const isPartial = partialIndex !== -1;
      const fileName = isPartial ? entry.name.slice(0, partialIndex) : entry.name;
      if (!isMediaFileName(fileName)) continue;
      if (isPartial) {
        partialEntries.set(fileName, entry.name);
      } else {
        finalSizes.set(fileName, entry.size);
      }
    }

    const items: Array<MediaListEntry> = [];
    for (const [fileName, sizeBytes] of finalSizes) {
      items.push({ media: { ...this.buildMediaItem(serial, yearMonth, fileName), syncState: SyncStateValues.COMPLETE }, sizeBytes });
    }
    // a final file wins over a leftover partial for the same name
    for (const [fileName, onDiskName] of partialEntries) {
      if (finalSizes.has(fileName)) continue;
      items.push({
        media: {
          ...this.buildMediaItem(serial, yearMonth, fileName),
          syncState: SyncStateValues.IN_PROGRESS,
          localFilePath: this.fileService.huntingProductMediaPath(serial, yearMonth, onDiskName),
        },
        sizeBytes: undefined,
      });
    }
    return items;
  }

  private async readdirSafe$(path: string): Promise<Array<FileInfo>> {
    try {
      const { files } = await Filesystem.readdir({ path, directory: Directory.Data });
      return files;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      // Capacitor's own wording when the parent directory does not exist yet — an empty listing, not a fault.
      if (message.includes('does not exist')) return [];
      throw error;
    }
  }

  /**
   * Wipes every synced hunting-product media entry from storage. Called on logout so a
   * different user on the same device never sees the previous user's synced photos/videos.
   */
  async clearHuntingProductMedia$(): Promise<void> {
    await this.deleteAll$();
  }
}
